<template>
   <div :class="{ isDisabled: disabled }">
      <InputLabel :id="id" :label="label" :showIsOptional="props.isOptional" />

      <div class="relative">
         <select
            :id="id"
            :value="selected"
            :name="id"
            class="custom-select"
            :class="{ 'has-error': props.hasError }"
            style="-webkit-appearance: none"
            :data-cy="id"
            :disabled="disabled"
            @input="onSelectedChange"
         >
            <option disabled value="">{{ placeholder || $t("system.placeholder_select_option") }}</option>
            <option
               v-for="(option, i) in options"
               :key="option.value"
               :data-cy="`option-${i}`"
               :selected="option.value === selectedOption ? true : false"
               :value="option.value"
            >
               {{ option.label }}
            </option>
         </select>

         <span class="dropdown-icon">
            <IconToggleArrow v-if="!disabled" class="shrink-0 fill-current text-current mr-1 size-3 rotate-90" />
            <IconPadlock v-else class="padlock-icon" />
         </span>
      </div>

      <!-- inline error -->
      <InvalidInputsList
         v-if="errorHelper && props.hasError"
         :errorList="errorList"
         :data-cy="`select-${id}-error-helper`"
      />
   </div>
</template>

<script setup lang="ts">
import { slugify } from "@/js/helpers/slugify";
import IconToggleArrow from "@/components/icons/IconToggleArrow.vue";
import IconPadlock from "@/components/icons/IconPadlock.vue";

const $emit = defineEmits(["optionSelected"]);

interface Props {
   selectedOption?: string;
   options: { value: string; label: string }[];
   placeholder?: string;
   label: string;
   disabled?: boolean;
   errorHelper?: { text: string; code: string };
   hasError?: boolean;
   isLabelVisible?: boolean;
   isOptional?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
   selectedOption: "",
   disabled: false,
   placeholder: "",
   hasError: false,
   isLabelVisible: true,
   isOptional: false,
});

const selected = ref(props.selectedOption);

const id = computed(() => slugify(props.label));

const errorList = computed(() => {
   if (!props.hasError) return [];
   return [
      {
         inputId: `select-${id.value}`,
         validator: props.errorHelper?.code,
         message: props.errorHelper?.text,
      },
   ];
});

watch(
   () => props.selectedOption,
   (newValue) => {
      if (selected.value !== newValue) {
         selected.value = newValue;
      }
   }
);

function onSelectedChange($event) {
   const newValue = $event.target.value;
   selected.value = newValue;
   $emit("optionSelected", newValue);
}
</script>

<style lang="scss" scoped>
$disabledColor: var(--grey-80);
$disabledBorderColor: var(--grey-20);
$customSelectBorderColor: var(--grey-20);
$customSelectBackgroundColor: var(--grey-10);
$customSelectErrorBorderColor: #ee4444; // temporary to be consistent with other components

.isDisabled select {
   color: $disabledColor;
   border-color: $disabledBorderColor;
}

.custom-select {
   box-sizing: border-box;
   padding-top: 0.5rem;
   padding-bottom: 0.5rem;
   padding-left: 1rem;
   padding-right: 2rem;
   width: 100%;
   border-radius: 0.375rem; // 6px
   border: 1px solid $customSelectBorderColor;
   background-color: $customSelectBackgroundColor;

   &.has-error {
      border-color: $customSelectErrorBorderColor;
   }
}

.dropdown-icon {
   position: absolute;
   top: 50%;
   right: 0.5rem;
   transform: translateY(-50%);
   pointer-events: none;
   color: var(--black);
}

.icon-open {
   transform: rotate(180deg);
   transition: transform 0.2s;
}

.icon-closed {
   transform: rotate(0deg);
   transition: transform 0.2s;
}

.disabled-icon {
   position: absolute;
   top: 50%;
   right: 0.5rem;
   transform: translateY(-50%);
   padding: 0.5rem;
}

.padlock-icon {
   margin-left: 0.25rem;
   width: 1.2rem;
   height: 1.2rem;
   flex-shrink: 0;
   fill: currentColor;
   color: currentColor;
   opacity: 0.3;
}
</style>
